// Projektnivå (SKR) beräknas av servern utifrån objektiva uppgifter.
// Nivån går inte att sätta manuellt från appen – databasen blockerar det.
// Den kan höjas automatiskt när projektet växer, men sänks aldrig av systemet.
import { supabase } from "@/integrations/supabase/client";
import type { DetailLevel } from "./detailLevel";

export interface ScaleFacts {
  contract_value: number | null;
  gross_area: number | null;
  duration_months: number | null;
  contractor_count: number | null;
  special_risk_count: number | null;
  prior_notification_required: boolean;
  street_address: string | null;
  property_designation: string | null;
  updated_at?: string | null;
}

export const EMPTY_SCALE_FACTS: ScaleFacts = {
  contract_value: null,
  gross_area: null,
  duration_months: null,
  contractor_count: null,
  special_risk_count: null,
  prior_notification_required: false,
  street_address: null,
  property_designation: null,
};

export interface ComputedLevel {
  level: DetailLevel;
  reasons: string[];
}

export interface LevelHistoryEntry {
  id: string;
  from_level: string | null;
  to_level: string;
  reasons: string[];
  source: string;
  created_at: string;
}

export interface SplitCandidate {
  sibling_id: string;
  sibling_name: string;
  match_reason: string;
  created_at: string;
  sibling_level: string | null;
}

export interface OverrideRequest {
  id: string;
  requested_level: string;
  computed_level: string | null;
  motivation: string;
  status: string;
  decision_note: string | null;
  created_at: string;
}

const asLevel = (value: unknown): DetailLevel =>
  value === "enkel" || value === "standard" || value === "full" ? value : "standard";

/** Läser de objektiva uppgifterna som nivån beräknas från. */
export const fetchScaleFacts = async (projectId: string): Promise<ScaleFacts> => {
  const { data } = await supabase
    .from("project_scale_facts")
    .select(
      "contract_value, gross_area, duration_months, contractor_count, special_risk_count, prior_notification_required, street_address, property_designation, updated_at"
    )
    .eq("project_id", projectId)
    .maybeSingle();

  if (!data) return { ...EMPTY_SCALE_FACTS };
  return {
    contract_value: data.contract_value,
    gross_area: data.gross_area,
    duration_months: data.duration_months,
    contractor_count: data.contractor_count,
    special_risk_count: data.special_risk_count,
    prior_notification_required: !!data.prior_notification_required,
    street_address: data.street_address,
    property_designation: data.property_designation,
    updated_at: data.updated_at,
  };
};

/** Sparar uppgifterna. Databasen räknar om nivån automatiskt via trigger. */
export const saveScaleFacts = async (projectId: string, facts: ScaleFacts) => {
  const { data: userRes } = await supabase.auth.getUser();
  const { error } = await supabase.from("project_scale_facts").upsert(
    {
      project_id: projectId,
      contract_value: facts.contract_value,
      gross_area: facts.gross_area,
      duration_months: facts.duration_months,
      contractor_count: facts.contractor_count,
      special_risk_count: facts.special_risk_count,
      prior_notification_required: facts.prior_notification_required,
      street_address: facts.street_address,
      property_designation: facts.property_designation,
      updated_by: userRes.user?.id ?? null,
    },
    { onConflict: "project_id" }
  );
  if (error) throw new Error(error.message);
};

/** Nivån som motorn räknar fram just nu (utan att spara). */
export const computeLevel = async (projectId: string): Promise<ComputedLevel> => {
  const { data, error } = await supabase.rpc("compute_project_level", {
    p_project_id: projectId,
  });
  if (error) throw new Error(error.message);
  const row = (data || [])[0] as { level: string; reasons: string[] } | undefined;
  return { level: asLevel(row?.level), reasons: row?.reasons ?? [] };
};

/** Räknar om och sparar nivån. Höjer vid behov, sänker aldrig. */
export const applyLevel = async (
  projectId: string,
  source = "manual_recalc"
): Promise<{ level: DetailLevel; changed: boolean; reasons: string[] }> => {
  const { data, error } = await supabase.rpc("apply_project_level", {
    p_project_id: projectId,
    p_source: source,
  });
  if (error) throw new Error(error.message);
  const row = (data || [])[0] as
    | { level: string; changed: boolean; reasons: string[] }
    | undefined;
  return {
    level: asLevel(row?.level),
    changed: !!row?.changed,
    reasons: row?.reasons ?? [],
  };
};

export const fetchLevelHistory = async (projectId: string): Promise<LevelHistoryEntry[]> => {
  const { data } = await supabase
    .from("project_level_history")
    .select("id, from_level, to_level, reasons, source, created_at")
    .eq("project_id", projectId)
    .order("created_at", { ascending: false })
    .limit(25);
  return (data || []) as LevelHistoryEntry[];
};

/** Projekt som ser ut att vara delar av samma bygge. */
export const fetchSplitCandidates = async (projectId: string): Promise<SplitCandidate[]> => {
  const { data, error } = await supabase.rpc("detect_project_splits", {
    p_project_id: projectId,
  });
  if (error) return [];
  return (data || []) as SplitCandidate[];
};

export const fetchOverrideRequests = async (projectId: string): Promise<OverrideRequest[]> => {
  const { data } = await supabase
    .from("project_level_override_requests")
    .select("id, requested_level, computed_level, motivation, status, decision_note, created_at")
    .eq("project_id", projectId)
    .order("created_at", { ascending: false });
  return (data || []) as OverrideRequest[];
};

/** Begär omprövning av nivån. Kräver skriftlig motivering och beslutas av admin. */
export const requestLevelOverride = async (
  projectId: string,
  requestedLevel: DetailLevel,
  motivation: string,
  computedLevel: DetailLevel
) => {
  const { data: userRes } = await supabase.auth.getUser();
  const { error } = await supabase.from("project_level_override_requests").insert({
    project_id: projectId,
    requested_level: requestedLevel,
    computed_level: computedLevel,
    motivation,
    requested_by: userRes.user?.id as string,
  });
  if (error) throw new Error(error.message);
};

/** Slår ihop flera projekt till ett gemensamt program – tar bort vinsten med att stycka. */
export const linkToProgram = async (projectIds: string[], programName: string) => {
  const { error } = await supabase
    .from("projects")
    .update({ program_name: programName })
    .in("id", projectIds);
  if (error) throw new Error(error.message);
};
